"use client";

import { usePathname, useRouter } from "next/navigation";
import React, { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import Splash from "../Splash/Splash";
import { useUserProfile } from "@/lib/hooks/useUser";
import toast from "react-hot-toast";
import { useTranslations } from "next-intl";
import { getCookie, isPublicRoute } from "@/lib/helpers";
import { SUPPORTED_LOCALES } from "@/lib/constants/locale";

export default function AuthRoute({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { userProfileQuery } = useUserProfile();
  const routeCheck = ["login", "logout", "callback"];
  const tToast = useTranslations("toast");

  useEffect(() => {
    if (
      !routeCheck.some((route) => pathname.includes(route)) &&
      !auth?.isLoading &&
      !auth?.isAuthenticated &&
      !auth.error &&
      !isPublicRoute(pathname)
    ) {
      // Grace window: if the access token just expired but a refresh token
      // is still on hand, let silent renew run instead of bouncing to login.
      if (auth.user?.refresh_token) {
        if (auth.activeNavigator !== "signinSilent") {
          auth.signinSilent().catch((err) => {
            console.error("Silent renew failed in AuthRoute", err);
            // Refresh token expired or rejected — fall back to a full login.
            auth.signinRedirect();
          });
        }
        return;
      }

      if (pathname.includes("download")) {
        const parts = pathname.split("/download/");
        const downloadId = parts[1];
        localStorage.setItem("downloadId", downloadId);
      } else if (pathname.includes("/patientpath/enrollments")) {
        const parts = pathname.split("/enrollments/");
        const enrollmentId = parts[1];
        localStorage.setItem("enrollmentId", enrollmentId);
      }
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error]);

  // if authenticated, reroute based on user type
  useEffect(() => {
    if (
      auth.isAuthenticated &&
      auth.user &&
      !pathname.includes("download") &&
      !isPublicRoute(pathname) &&
      userProfileQuery.data &&
      !pathname.includes("expired")
    ) {
      const userRole = userProfileQuery.data.role;
      if (userRole) {
        const pathParts = pathname.split("/");

        const expectedRole = userRole === "scheduler" ? "provider" : userRole;
        const currentRole = pathParts[2];

        const pathLocale = pathParts[1];
        const resolvedLocale = SUPPORTED_LOCALES.some(
          (l) => l.value === pathLocale,
        )
          ? pathLocale
          : (getCookie("user-locale-preference") ?? "en-US");

        if (currentRole === "admin" && userRole !== "admin") {
          router.replace(
            userRole === "patient"
              ? `/${resolvedLocale}/patient/dashboard`
              : `/${resolvedLocale}/provider/overview`,
          );
        } else if (currentRole !== expectedRole) {
          pathParts[1] = resolvedLocale;
          pathParts[2] = expectedRole;
          pathParts[3] =
            expectedRole === "patient"
              ? "dashboard"
              : expectedRole === "admin"
                ? "organizations"
                : "overview";

          const newPath = pathParts.join("/");
          if (newPath !== pathname) {
            router.replace(newPath);
          }
        }
      } else {
        toast(tToast("userRole.noRole"));
        auth.signinRedirect();
      }
    }
  }, [
    auth.isAuthenticated,
    pathname,
    router,
    auth.user,
    userProfileQuery.data,
  ]);

  if ((auth.isLoading && !auth.isAuthenticated) || userProfileQuery.isLoading) {
    return <Splash message="Loading..." />;
  }

  return children;
}
